import type { User } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	OptionalUserEqualsFilterModifier,
	OptionalUserExcludesFilterModifier,
	OptionalUserIncludesFilterModifier,
	OptionalUserNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { AssigneeFilterCell } from "./AssigneeFilterCell"

type AssigneeFieldValue = User["id"] | null
type AssigneeFilterValue = (User["id"] | null)[] | User["id"] | null

export class AssigneeFilterDef extends BaseFilterDef<AssigneeFieldValue, AssigneeFilterValue> {
	readonly id = "assigned_to"
	readonly defaultFilterValue: AssigneeFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly assignees: User[]

	constructor(assignees: User[]) {
		super()
		this.assignees = assignees
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: OptionalUserEqualsFilterModifier,
			isValueValid: (_value: AssigneeFieldValue): _value is User["id"] | null => true,
			isFilterValueValid: (filterValue: AssigneeFilterValue): filterValue is User["id"] | null =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: OptionalUserNotEqualsFilterModifier,
			isValueValid: (_value: AssigneeFieldValue): _value is User["id"] | null => true,
			isFilterValueValid: (filterValue: AssigneeFilterValue): filterValue is User["id"] | null =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: OptionalUserIncludesFilterModifier,
			isValueValid: (_value: AssigneeFieldValue): _value is User["id"] | null => true,
			isFilterValueValid: (filterValue: AssigneeFilterValue): filterValue is (User["id"] | null)[] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: OptionalUserExcludesFilterModifier,
			isValueValid: (_value: AssigneeFieldValue): _value is User["id"] | null => true,
			isFilterValueValid: (filterValue: AssigneeFilterValue): filterValue is (User["id"] | null)[] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<AssigneeFilterDef>): ReactNode {
		return <AssigneeFilterCell key={props.filter.id} {...props} />
	}
}
